import { DateTime } from 'luxon';
import type { UseFormSetValue } from 'react-hook-form';
import { v4 } from 'uuid';
import type { GeneratedGame } from '@/components/Event/GeneratedGames/GeneratedGame.types';
import type { OnboardingPersona } from '@/stores/useOnboardingStore';
import getRandomColor from '@/utils/getRandomColor';
import palette from '@/utils/palette';
import { getRandomTeamNameSuggestions } from '@/utils/teamNameSuggestions';

export type WelcomeTeamRow = {
  color: string;
  id: string;
  localAvatarUrl: string | null;
  title: string;
};

export type WelcomeScheduleSlotRow = {
  id: string;
  datetime: string;
  location: string;
};

export type WelcomeFormValues = {
  organizationDraftId: string;
  leagueDraftId: string;
  seasonDraftId: string;
  standaloneTeamDraftId: string;
  username: string;
  firstName: string;
  lastName: string;
  userColor: string;
  userLocalAvatarUrl: string | null;
  age: boolean;
  persona: OnboardingPersona | null;
  inviteCode: string;
  organizationTitle: string;
  organizationTagGroupId: string | null;
  leagueTitle: string;
  seasonTitle: string;
  seasonStartDate: string;
  seasonHasStandings: boolean;
  orgTeamRows: WelcomeTeamRow[];
  scheduleSlots: WelcomeScheduleSlotRow[];
  generatedScheduleGames: GeneratedGame[];
  scheduleMatchupCount: number;
  scheduleRepeatEveryWeeks: number;
  teamTitle: string;
  teamColor: string;
  teamLocalAvatarUrl: string | null;
  teamTagGroupId: string | null;
};

export const WELCOME_DEFAULT_LEAGUE_TITLE = 'Main League';
const WELCOME_DEFAULT_SCHEDULE_MATCHUP_COUNT = 1;
const WELCOME_DEFAULT_SCHEDULE_REPEAT_EVERY_WEEKS = 1;
const WELCOME_FORM_UPDATE_OPTIONS = {
  shouldDirty: true,
  shouldValidate: true,
};

type WelcomeFormSetValue = UseFormSetValue<WelcomeFormValues>;

export function getWelcomeDefaultSeasonTitle() {
  return `${DateTime.now().year} Season`;
}

export function getWelcomeDefaultSeasonStartDate() {
  return DateTime.now().plus({ weeks: 1 }).toFormat('yyyy-MM-dd');
}

export function getRandomWelcomeTeamColor(existingColors: string[] = []) {
  const availableColors = palette.filter(
    (paletteColor) => !existingColors.includes(paletteColor),
  );
  const colorOptions = availableColors.length > 0 ? availableColors : palette;

  return colorOptions[Math.floor(Math.random() * colorOptions.length)];
}

export function createWelcomeTeamRow(
  title = '',
  existingColors: string[] = [],
): WelcomeTeamRow {
  return {
    color: getRandomWelcomeTeamColor(existingColors),
    id: v4(),
    localAvatarUrl: null,
    title,
  };
}

export function createWelcomeTeamRows(named = false) {
  const titles = named ? getRandomTeamNameSuggestions({ count: 4 }) : [];
  const colors: string[] = [];

  return Array.from({ length: 4 }, (_, index) => {
    const row = createWelcomeTeamRow(titles[index] ?? '', colors);

    colors.push(row.color);

    return row;
  });
}

export function getWelcomeScheduleSlotCount(teamCount: number) {
  return Math.max(1, Math.floor(teamCount / 2));
}

export function createWelcomeScheduleSlots(
  teamCount: number,
  startDate: string | null,
) {
  const slotCount = getWelcomeScheduleSlotCount(teamCount);
  const baseDate = startDate
    ? DateTime.fromISO(startDate).set({ hour: 18, minute: 0 })
    : DateTime.now().plus({ weeks: 1 }).startOf('hour');

  return Array.from({ length: slotCount }, (_, index) => ({
    id: v4(),
    datetime: baseDate.plus({ hours: index }).toISO() ?? '',
    location: '',
  }));
}

export function createWelcomeFormDefaultValues(): WelcomeFormValues {
  return {
    organizationDraftId: v4(),
    leagueDraftId: v4(),
    seasonDraftId: v4(),
    standaloneTeamDraftId: v4(),
    username: '',
    firstName: '',
    lastName: '',
    userColor: getRandomColor(),
    userLocalAvatarUrl: null,
    age: false,
    persona: null,
    inviteCode: '',
    organizationTitle: '',
    organizationTagGroupId: null,
    leagueTitle: WELCOME_DEFAULT_LEAGUE_TITLE,
    seasonTitle: getWelcomeDefaultSeasonTitle(),
    seasonStartDate: getWelcomeDefaultSeasonStartDate(),
    seasonHasStandings: true,
    orgTeamRows: createWelcomeTeamRows(true),
    scheduleSlots: [],
    generatedScheduleGames: [],
    scheduleMatchupCount: WELCOME_DEFAULT_SCHEDULE_MATCHUP_COUNT,
    scheduleRepeatEveryWeeks: WELCOME_DEFAULT_SCHEDULE_REPEAT_EVERY_WEEKS,
    teamTitle: '',
    teamColor: getRandomColor(),
    teamLocalAvatarUrl: null,
    teamTagGroupId: null,
  };
}

export function resetWelcomeInviteDraft(setValue: WelcomeFormSetValue) {
  setValue('inviteCode', '', WELCOME_FORM_UPDATE_OPTIONS);
}

export function resetWelcomeTeamDraft(setValue: WelcomeFormSetValue) {
  setValue('standaloneTeamDraftId', v4(), WELCOME_FORM_UPDATE_OPTIONS);
  setValue('teamTitle', '', WELCOME_FORM_UPDATE_OPTIONS);
  setValue('teamColor', getRandomColor(), WELCOME_FORM_UPDATE_OPTIONS);
  setValue('teamLocalAvatarUrl', null, WELCOME_FORM_UPDATE_OPTIONS);
  setValue('teamTagGroupId', null, WELCOME_FORM_UPDATE_OPTIONS);
}

export function resetWelcomeScheduleDraft(setValue: WelcomeFormSetValue) {
  setValue('scheduleSlots', [], WELCOME_FORM_UPDATE_OPTIONS);
  setValue('generatedScheduleGames', [], WELCOME_FORM_UPDATE_OPTIONS);
  setValue(
    'scheduleMatchupCount',
    WELCOME_DEFAULT_SCHEDULE_MATCHUP_COUNT,
    WELCOME_FORM_UPDATE_OPTIONS,
  );
  setValue(
    'scheduleRepeatEveryWeeks',
    WELCOME_DEFAULT_SCHEDULE_REPEAT_EVERY_WEEKS,
    WELCOME_FORM_UPDATE_OPTIONS,
  );
}

export function resetWelcomeOrganizationDraft(setValue: WelcomeFormSetValue) {
  setValue('organizationDraftId', v4(), WELCOME_FORM_UPDATE_OPTIONS);
  setValue('leagueDraftId', v4(), WELCOME_FORM_UPDATE_OPTIONS);
  setValue('seasonDraftId', v4(), WELCOME_FORM_UPDATE_OPTIONS);
  setValue('organizationTitle', '', WELCOME_FORM_UPDATE_OPTIONS);
  setValue('organizationTagGroupId', null, WELCOME_FORM_UPDATE_OPTIONS);
  setValue(
    'leagueTitle',
    WELCOME_DEFAULT_LEAGUE_TITLE,
    WELCOME_FORM_UPDATE_OPTIONS,
  );
  setValue(
    'seasonTitle',
    getWelcomeDefaultSeasonTitle(),
    WELCOME_FORM_UPDATE_OPTIONS,
  );
  setValue(
    'seasonStartDate',
    getWelcomeDefaultSeasonStartDate(),
    WELCOME_FORM_UPDATE_OPTIONS,
  );
  setValue('seasonHasStandings', true, WELCOME_FORM_UPDATE_OPTIONS);
  setValue(
    'orgTeamRows',
    createWelcomeTeamRows(true),
    WELCOME_FORM_UPDATE_OPTIONS,
  );
  resetWelcomeScheduleDraft(setValue);
}

export function resetWelcomeSetupDrafts(setValue: WelcomeFormSetValue) {
  resetWelcomeInviteDraft(setValue);
  resetWelcomeTeamDraft(setValue);
  resetWelcomeOrganizationDraft(setValue);
}

export function resetWelcomeSetup(setValue: WelcomeFormSetValue) {
  setValue('persona', null, WELCOME_FORM_UPDATE_OPTIONS);
  resetWelcomeSetupDrafts(setValue);
}
